import { useEffect, useRef, useState } from 'react'

import { getStoredIsHighContrastMode } from '../../lib/localStorage'

type Props = {
  // Number of guesses submitted so far.
  guessCount: number
  // Hide hints once the game is over (the answer is revealed anyway).
  gameOver: boolean
}

type Hint = { threshold: number; text: string }

// Custom-written hints for the fixed YALLS answer, revealed progressively.
const HINTS: Hint[] = [
  { threshold: 3, text: 'Who we love' },
  { threshold: 5, text: 'How to refer to a group of people in Texas' },
]

// Animated top banner that pops in each time a new hint unlocks (after the
// 3rd and 5th guesses). Shows all unlocked hints; re-triggers the pop
// animation when the newest one changes.
export const Hints = ({ guessCount, gameOver }: Props) => {
  // Keep the hints rendered at game end so they can fade out instead of
  // vanishing the instant the last row is entered.
  const unlocked = HINTS.filter((h) => guessCount >= h.threshold)

  const newestThreshold = unlocked.length
    ? unlocked[unlocked.length - 1].threshold
    : 0

  const [animate, setAnimate] = useState(false)
  const prevNewest = useRef(0)

  useEffect(() => {
    // Re-run the pop animation only when a NEW hint has just unlocked.
    if (newestThreshold > prevNewest.current) {
      setAnimate(false)
      // next frame -> add the class so the animation restarts
      const id = requestAnimationFrame(() => setAnimate(true))
      prevNewest.current = newestThreshold
      return () => cancelAnimationFrame(id)
    }
  }, [newestThreshold])

  if (unlocked.length === 0) return null

  // Hints are partial information, so each is marked with a small "present"
  // tile (yellow, or cyan in high-contrast mode) carrying its number.
  const tileColor = getStoredIsHighContrastMode()
    ? 'bg-cyan-500'
    : 'bg-yellow-500'

  return (
    <div
      className={`pointer-events-none fixed inset-x-0 top-0 z-20 flex justify-center px-3 transition-opacity duration-500 ease-out ${
        gameOver ? 'opacity-0' : 'opacity-100'
      }`}
      aria-live="polite"
      aria-hidden={gameOver}
    >
      <div
        className={`mt-2 w-full max-w-sm rounded-2xl border border-gray-200 bg-white px-4 py-3 text-left shadow-xl dark:border-slate-700 dark:bg-slate-800 ${
          animate ? 'hint-pop' : ''
        }`}
      >
        {unlocked.map((h, i) => (
          <div
            key={h.threshold}
            className={`flex items-center gap-3 ${i > 0 ? 'mt-2' : ''}`}
          >
            <span
              className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-sm font-bold text-white ${tileColor}`}
            >
              {i + 1}
            </span>
            <p className="text-base font-semibold leading-snug text-gray-900 dark:text-white">
              <span className="font-medium text-gray-500 dark:text-gray-400">
                Hint:{' '}
              </span>
              {h.text}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}
